// SPDX-License-Identifier: GPL-3.0-or-later
//
// Tiny Mustache-shaped template engine with built-in Typst escaping.
// Identical logic to the previous LaTeX renderer — only the escape function
// has changed. Mustache `{{...}}` delimiters don't collide with Typst syntax
// (Typst uses #, $, [, ], *, _, backticks for its own markers).
//
// Syntax:
//   {{var}}           — Typst-escaped substitution
//   {{{var}}}         — raw substitution (NO escape; use only for trusted text)
//   {{&var}}          — alias for raw
//   {{#section}} … {{/section}}  — iterate array OR render once if truthy non-array
//   {{^section}} … {{/section}}  — inverse: render if section is missing / empty
//   {{.}}             — current item inside a section (when iterating strings)
//
// Dot-paths supported: {{contact.email}}, {{user.address.city}}.

import { escapeTypst } from './escape';

type Ctx = unknown;
type Scope = Ctx[];

function lookup(scope: Scope, path: string): unknown {
  if (path === '.') return scope[scope.length - 1];
  const parts = path.split('.');
  for (let i = scope.length - 1; i >= 0; i--) {
    let cur: unknown = scope[i];
    let ok = true;
    for (const p of parts) {
      if (cur == null || typeof cur !== 'object') { ok = false; break; }
      cur = (cur as Record<string, unknown>)[p];
      if (cur === undefined) { ok = false; break; }
    }
    if (ok) return cur;
  }
  return undefined;
}

function truthy(v: unknown): boolean {
  if (v === undefined || v === null || v === false) return false;
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

interface Block {
  start: number;
  end: number;
  name: string;
  inverse: boolean;
  innerStart: number;
  innerEnd: number;
}

function findBlock(src: string, from: number): Block | null {
  const open = /\{\{([#^])\s*([\w.]+)\s*\}\}/g;
  open.lastIndex = from;
  const m = open.exec(src);
  if (!m) return null;
  const name = m[2]!;
  const inverse = m[1] === '^';
  const innerStart = m.index + m[0].length;
  const closeTag = new RegExp(`\\{\\{[#^]\\s*${escapeRegExp(name)}\\s*\\}\\}|\\{\\{/\\s*${escapeRegExp(name)}\\s*\\}\\}`, 'g');
  closeTag.lastIndex = innerStart;
  let depth = 1;
  let cm: RegExpExecArray | null;
  while ((cm = closeTag.exec(src))) {
    if (cm[0].startsWith('{{/')) {
      depth--;
      if (depth === 0) {
        return {
          start: m.index,
          end: cm.index + cm[0].length,
          name,
          inverse,
          innerStart,
          innerEnd: cm.index,
        };
      }
    } else {
      depth++;
    }
  }
  throw new Error(`Unclosed template section: ${name}`);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderInner(src: string, scope: Scope): string {
  let out = '';
  let i = 0;
  while (i < src.length) {
    const block = findBlock(src, i);
    if (!block) {
      out += renderVars(src.slice(i), scope);
      break;
    }
    out += renderVars(src.slice(i, block.start), scope);
    const value = lookup(scope, block.name);
    const inner = src.slice(block.innerStart, block.innerEnd);
    if (block.inverse) {
      if (!truthy(value)) out += renderInner(inner, scope);
    } else if (Array.isArray(value)) {
      for (const item of value) {
        out += renderInner(inner, [...scope, item]);
      }
    } else if (truthy(value)) {
      out += renderInner(inner, [...scope, value as Ctx]);
    }
    i = block.end;
  }
  return out;
}

function renderVars(src: string, scope: Scope): string {
  return src.replace(
    /\{\{\{\s*([\w.]+)\s*\}\}\}|\{\{&\s*([\w.]+)\s*\}\}|\{\{\s*([\w.]+)\s*\}\}/g,
    (_match, raw1: string | undefined, raw2: string | undefined, escapedName: string | undefined) => {
      const raw = raw1 ?? raw2;
      const path = raw ?? escapedName!;
      const v = lookup(scope, path);
      if (v === undefined || v === null) return '';
      const s = typeof v === 'string' ? v : Array.isArray(v) ? v.join(', ') : String(v);
      return raw !== undefined ? s : escapeTypst(s);
    },
  );
}

export function renderTemplate(template: string, data: unknown): string {
  return renderInner(template, [data]);
}
